import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import InfiniteMessages from '@/modules/messages/client/components/InfiniteMessages';
import Avatar from '@/modules/users/client/components/Avatar.component';
import ThreadMessage from '@/modules/messages/client/components/ThreadMessage';
import { userType } from '@/modules/users/client/users.prop-types';

const MessagesContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding-top: 30px;
  padding-right: 30px;
  @media (min-width: 768px) {
    padding-right: 0;
  }
`;

export default function ThreadMessages({
  user,
  otherUser,
  messages,
  onFetchMore,
}) {
  const { t } = useTranslation('messages');
  const isExtraSmall = useMediaQuery({ maxWidth: 768 - 1 });
  const [hasScrolled, setHasScrolled] = useState(false);

  function extractHostingRequestId(content) {
    const match = content.match(/<b>Request ID:<\/b>\s*([^<]+)<\/p>/);
    return match ? match[1].trim() : null;
  }

  const scrollToMessage = messageId => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      // Scroll to the message
      messageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Add highlight effect
      messageElement.style.transition = 'all 0.5s ease';
      messageElement.style.backgroundColor = '#f0f8ff';
      messageElement.style.boxShadow = '0 0 0 2px #007bff';

      // Remove highlight after 3 seconds
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
        messageElement.style.boxShadow = '';
      }, 3000);

      setHasScrolled(true);
    }
  };

  const findAndScrollToRequestMessage = requestId => {
    // Find all messages that might contain this request ID
    const messageElements = document.querySelectorAll('.message');

    for (const messageElement of messageElements) {
      const contentElement = messageElement.querySelector('.panel-body');
      if (contentElement) {
        const extractedRequestId = extractHostingRequestId(
          contentElement.innerHTML,
        );
        if (extractedRequestId === requestId) {
          const foundMessageId = messageElement.id.replace('message-', '');
          scrollToMessage(foundMessageId);
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    if (messages.length === 0 || hasScrolled) return;

    // Get messageId from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const messageId = urlParams.get('messageId');
    const requestId = urlParams.get('requestId');

    if (messageId) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        scrollToMessage(messageId);
      }, 300);
    } else if (requestId) {
      // Longer delay to ensure all messages are loaded and rendered
      setTimeout(() => {
        const found = findAndScrollToRequestMessage(requestId);
        if (!found) {
          // If not found, try again after a longer delay
          setTimeout(() => {
            findAndScrollToRequestMessage(requestId);
          }, 1000);
        }
      }, 500);
    }
  }, [messages, hasScrolled]); // Run when messages change or component mounts

  return (
    <InfiniteMessages component={MessagesContainer} onFetchMore={onFetchMore}>
      {isExtraSmall && otherUser.username && (
        <div className="message">
          <div className="message-recipient panel panel-default">
            <a className="panel-body" href={`/profile/${otherUser.username}`}>
              <Avatar user={otherUser} size={32} link={false} />
              <h4>{otherUser.displayName}</h4>
              <small className="text-muted">@{otherUser.username}</small>
            </a>
          </div>
        </div>
      )}
      <div className="message">
        <div className="divider divider-first text-muted">
          <small>
            {t('Conversation started {{ date }}', {
              date: moment(messages[0].created).format('LL'),
            })}
          </small>
        </div>
      </div>
      {messages.map(message => (
        <ThreadMessage key={message._id} message={message} user={user} />
      ))}
    </InfiniteMessages>
  );
}

ThreadMessages.propTypes = {
  user: userType.isRequired,
  otherUser: userType.isRequired,
  messages: PropTypes.array.isRequired,
  onFetchMore: PropTypes.func.isRequired,
};
